import React from 'react';
import Meter from './Meter';
import { TYPE } from '../../constants/typeScale';

/**
 * STORAGE BAR — how much room the creator's media is using, in plain megabytes.
 *
 * Owner 2026-08-23: "remove the concept of HP, just ask user to upgrade when they reach storage
 * limit." The quota itself is unchanged and still enforced server-side; what is gone is the invented
 * unit. Nobody should have to learn a second currency to understand they are out of room — so this
 * says MB, and when it is full it stops being a readout and becomes an upgrade prompt.
 *
 * Presentational: every number and the upgrade action arrive as props.
 */
export interface StorageBarProps {
  /** Megabytes stored. */
  usedMb: number;
  /** The plan's megabyte allowance — the denominator. */
  capMb: number;
  loading?: boolean;
  /** Rendered as the upgrade affordance when the quota is full (the caller owns the destination). */
  upgrade?: React.ReactNode;
}

/** MB up to a gigabyte, then GB with one decimal — a creator reads room, not arithmetic. */
export const formatMb = (mb: number): string =>
  mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb * 10) / 10} MB`;

const StorageBar: React.FC<StorageBarProps> = ({ usedMb, capMb, loading, upgrade }) => {
  const filled = Math.min(100, Math.round((usedMb / Math.max(1, capMb)) * 100));
  const full = !loading && usedMb >= capMb;
  return (
    <div data-testid="storage-bar">
      <Meter percent={filled} label="Storage used" loading={loading} testId="storage-bar" />
      <p className={`mt-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`} data-testid="storage-bar-line">
        {loading
          ? 'Checking your storage…'
          : full
            ? 'Your storage is full — upgrade for much more room, or delete media you no longer need.'
            : `Using ${formatMb(usedMb)} of ${formatMb(capMb)} for your media and files.`}
      </p>
      {full && upgrade}
    </div>
  );
};

export default StorageBar;
